import { apiClient } from '@/core/apiClient';
import type { Shelter, SheltersListParams } from './types';

const isFormData = (payload: unknown): payload is FormData =>
  typeof FormData !== 'undefined' && payload instanceof FormData;

export const sheltersApi = {
  search: async (params: { q: string; limit?: number; skip?: number }) => {
    const { q, limit = 20, skip = 0 } = params;
    const response = await apiClient.get('/shelters/search', {
      params: { q, limit, skip },
    });
    return response.data;
  },
  list: async (params: SheltersListParams = {}) => {
    const query: any = { ...params };
    if (params.q) {
      query["where[name][like]"] = params.q;
    }
    if (params.pageSize) {
      query["filter[limit]"] = params.pageSize;
    }
    if (params.page && params.pageSize) {
      query["filter[offset]"] = (params.page - 1) * params.pageSize;
    }
    const hasInclude = Object.keys(params.filter || {}).some((k) => k.startsWith('include'));
    if (!hasInclude) {
      query["filter[include][0][relation]"] = 'city';
    }
    if (params.filter) {
      // merge provided filter keys like filter[fields][name], filter[order], etc.
      Object.entries(params.filter).forEach(([k, v]) => (query[`filter[${k}]`] = v as any));
    }
    const response = await apiClient.get('/shelters', { params: query });
    return response.data;
  },

  getById: async (id: number): Promise<Shelter> => {
    const response = await apiClient.get(`/shelters/${id}`, {
      params: { 'filter[include][0][relation]': 'city' },
    });
    const body = response.data;
    return body?.data ?? body; // accept wrapped or raw
  },
  getBySlug: async (slug: string): Promise<Shelter> => {
    const response = await apiClient.get(`/shelters/slug/${slug}`, {
      params: { include: 'city' },
    });
    const body = response.data;
    return body?.data ?? body;
  },

  create: async (shelter: Shelter | FormData): Promise<Shelter> => {
    const now = new Date().toISOString();
    const payload = isFormData(shelter)
      ? shelter
      : {
          createdAt: shelter.createdAt ?? now,
          updatedAt: shelter.updatedAt ?? now,
          ...shelter,
        };
    if (isFormData(payload)) {
      if (!payload.has('createdAt')) payload.append('createdAt', now);
      if (!payload.has('updatedAt')) payload.append('updatedAt', now);
    }
    const response = await apiClient.post('/shelters', payload, isFormData(payload) ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined);
    const body = response.data;
    return body?.data ?? body;
  },

  update: async (id: number, shelter: Partial<Shelter> | FormData): Promise<Shelter> => {
    const now = new Date().toISOString();
    const payload = isFormData(shelter)
      ? shelter
      : {
          ...shelter,
          updatedAt: shelter.updatedAt ?? now,
        };
    if (isFormData(payload) && !payload.has('updatedAt')) payload.append('updatedAt', now);
    const response = await apiClient.patch(`/shelters/${id}`, payload, isFormData(payload) ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined);
    const body = response.data;
    return body?.data ?? body;
  },

  replace: async (id: number, shelter: Shelter): Promise<Shelter> => {
    const payload = {
      ...shelter,
      updatedAt: shelter.updatedAt ?? new Date().toISOString(),
    };
    const response = await apiClient.put(`/shelters/${id}`, payload);
    const body = response.data;
    return body?.data ?? body;
  },

  delete: async (id: number) => {
    await apiClient.delete(`/shelters/${id}`);
  },

  count: async (where?: Record<string, any>) => {
    const response = await apiClient.get('/shelters/count', { params: where ? { where } : undefined });
    return response.data;
  },

  updateBulk: async (where: Record<string, any>, data: Partial<Shelter>) => {
    const payload = {
      ...data,
      updatedAt: data.updatedAt ?? new Date().toISOString(),
    };
    const response = await apiClient.patch('/shelters', payload, { params: { where } });
    return response.data;
  },
};
